import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Gauge, Fuel, Calendar, Eye, Heart } from 'lucide-react';
import { API, useAuth } from '../App';
import axios from 'axios';
import { useTranslation } from '../LanguageContext';

const resolveImageUrl = (url) => {
  if (!url) return 'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=800';
  if (url.startsWith('/api/')) return `${API.replace('/api', '')}${url}`;
  return url;
};

const CarCard = ({ car, featured = false, favoriteIds = [], onFavToggle }) => {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const isFav = favoriteIds.includes(car.id);

  const toggleFav = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user && !token) return;
    try {
      const r = await axios.post(`${API}/favorites/${car.id}`, {}, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (onFavToggle) onFavToggle(car.id, r.data.favorited);
    } catch { }
  };
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatMileage = (mileage) => {
    return new Intl.NumberFormat('en-US').format(mileage);
  };

  return (
    <Link 
      to={`/cars/${car.id}`}
      className={`group relative overflow-hidden rounded-xl bg-black/40 backdrop-blur-sm border border-white/5 hover:border-cyan-500/50 transition-all duration-500 ${featured ? 'ring-1 ring-cyan-500/20' : ''}`}
      data-testid={`car-card-${car.id}`}
    >
      {/* Image Container */}
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={resolveImageUrl(car.images?.[0])}
          alt={car.title}
          className="w-full h-full object-cover car-image-stylized group-hover:scale-105 transition-transform duration-700"
        />
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-40 transition-opacity"></div>
        
        {/* Condition Badge */}
        <div className="absolute top-2 sm:top-3 left-2 sm:left-3">
          <span className={`px-2 sm:px-3 py-0.5 sm:py-1 text-[9px] sm:text-xs font-bold uppercase tracking-wider rounded ${car.condition === 'new' ? 'bg-green-500 text-black' : 'bg-white/20 text-white backdrop-blur-sm'}`}>
            {car.condition === 'new' ? t('card.new') : t('card.used')}
          </span>
        </div>

        {/* Featured Badge */}
        {featured && (
          <div className="absolute top-2 sm:top-3 right-2 sm:right-3">
            <span className="px-2 sm:px-3 py-0.5 sm:py-1 text-[9px] sm:text-xs font-bold uppercase tracking-wider rounded bg-cyan-500 text-black">
              {t('card.featured')}
            </span>
          </div>
        )}

        {/* Favorite Button */}
        {token && (
          <button onClick={toggleFav} className={`absolute ${featured ? 'top-8 sm:top-10' : 'top-2 sm:top-3'} right-2 sm:right-3 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-all z-10 ${isFav ? 'bg-red-500/90 text-white' : 'bg-black/50 text-white/70 hover:bg-black/70 hover:text-white backdrop-blur-sm'}`} data-testid={`fav-btn-${car.id}`}>
            <Heart className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${isFav ? 'fill-white' : ''}`} />
          </button>
        )}

        {/* Views */}
        <div className="absolute bottom-3 right-3 flex items-center gap-1 text-white/70 text-xs">
          <Eye className="w-3 h-3" />
          {car.views}
        </div>
      </div>

      {/* Content */}
      <div className="p-2.5 sm:p-4">
        {/* Title */}
        <h3 className="font-heading text-xs sm:text-lg font-semibold text-white group-hover:text-cyan-500 transition-colors line-clamp-1 mb-1 sm:mb-3">
          {car.title}
        </h3>

        <p className="text-base sm:text-2xl font-bold text-cyan-500 neon-text mb-2 sm:mb-3">
          {formatPrice(car.price)}
        </p>

        {/* Specs */}
        <div className="grid grid-cols-2 gap-1 sm:gap-2 mb-2 sm:mb-3">
          <div className="flex items-center gap-1 sm:gap-2 text-gray-400 text-[10px] sm:text-sm">
            <Calendar className="w-3 sm:w-4 h-3 sm:h-4 text-cyan-500/70 shrink-0" />
            {car.year}
          </div>
          <div className="flex items-center gap-1 sm:gap-2 text-gray-400 text-[10px] sm:text-sm">
            <Gauge className="w-3 sm:w-4 h-3 sm:h-4 text-cyan-500/70 shrink-0" />
            <span className="truncate">{formatMileage(car.mileage)} km</span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 text-gray-400 text-[10px] sm:text-sm">
            <Fuel className="w-3 sm:w-4 h-3 sm:h-4 text-cyan-500/70 shrink-0" />
            {car.fuel_type}
          </div>
          <div className="flex items-center gap-1 sm:gap-2 text-gray-400 text-[10px] sm:text-sm">
            <svg className="w-3 sm:w-4 h-3 sm:h-4 text-cyan-500/70 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="6" width="20" height="12" rx="2"/>
              <path d="M12 6v12M7 10v4M17 10v4"/>
            </svg>
            <span className="truncate">{car.transmission}</span>
          </div>
        </div>

        {/* Location */}
        <div className="flex items-center gap-1 text-gray-500 text-[10px] sm:text-sm pt-2 sm:pt-3 border-t border-white/5">
          <MapPin className="w-3 h-3 shrink-0" />
          <span className="truncate">{car.location}</span>
        </div>
      </div>

      {/* Hover effect line */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
    </Link>
  );
};

export default CarCard;
